// useDatabaseAutomationsState.js

import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { subscribeToAutomationRules } from 'services/automations/AutomationReadService';
import {
  clearLegacyDatabaseAutomations,
  deleteAutomationRuleByKey,
  saveAutomationRule,
} from 'services/automations/AutomationWriteService';
import { isSystemTypeAutomation } from 'services/automations/TypeAutomationService';

import {
  getFieldKey,
  getFieldLabel,
  normalizeFieldChecklistItemOptions,
  normalizeFieldEnumOptions,
} from '../utils/automationFieldUtils';
import {
  buildDefaultRule,
  getAutomationRuleTitle,
  normalizeRule,
  normalizeRulesForSave,
} from '../utils/automationRuleUtils';

const compareAutomationRules = (a, b) => {
  const labelA = getAutomationRuleTitle(a);
  const labelB = getAutomationRuleTitle(b);

  const labelCompare = labelA.localeCompare(labelB, undefined, { sensitivity: 'base' });
  if (labelCompare !== 0) return labelCompare;

  return String(a?.key || '').localeCompare(String(b?.key || ''), undefined, { sensitivity: 'base' });
};

export default function useDatabaseAutomationsState({
  isOpen,
  activeDatabase,
  baseKey,
  typeKey,
  resolvedProperties,
  userKey,
  onSaved,
}) {
  const [rules, setRules] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const databaseKey = String(activeDatabase?.key || '').trim();
  const resolvedBaseKey = String(baseKey || activeDatabase?.baseKey || '').trim();

  const fieldOptions = useMemo(() => {
    if (!Array.isArray(resolvedProperties)) return [];

    return resolvedProperties
      .map((field) => {
        const type = String(field?.type || '').trim().toLowerCase();
        return {
          key: getFieldKey(field),
          label: getFieldLabel(field),
          type,
          enumOptions: type === 'checklist'
            ? normalizeFieldChecklistItemOptions(field)
            : normalizeFieldEnumOptions(field),
        };
      })
      .filter((field) => field.key);
  }, [resolvedProperties]);

  const ruleFieldOptions = useMemo(() => (
    fieldOptions.filter((field) => field.type !== 'people')
  ), [fieldOptions]);

  const defaultFieldKey = useMemo(() => {
    return String(ruleFieldOptions[0]?.key || '').trim();
  }, [ruleFieldOptions]);

  const fieldTypeByKey = useMemo(() => {
    const map = new Map();
    fieldOptions.forEach((field) => {
      map.set(field.key, field.type || 'string');
    });
    return map;
  }, [fieldOptions]);

  const fieldEnumOptionsByKey = useMemo(() => {
    const map = new Map();
    fieldOptions.forEach((field) => {
      map.set(field.key, Array.isArray(field.enumOptions) ? field.enumOptions : []);
    });
    return map;
  }, [fieldOptions]);

  const hydrateRules = useCallback((rawRules) => {
    if (!Array.isArray(rawRules) || rawRules.length === 0) return [];
    return rawRules.map((rule) => {
      const normalized = normalizeRule(rule, { databaseKey, typeKey, defaultFieldKey });
      if (!isSystemTypeAutomation(rule)) return normalized;
      return {
        ...normalized,
        isSystemAutomation: true,
        automationSource: 'SYSTEM_TYPE',
        canDelete: false,
        enabled: rule?.enabled !== false,
      };
    });
  }, [databaseKey, defaultFieldKey, typeKey]);

  useEffect(() => {
    if (!isOpen) return;

    const fallbackRules = Array.isArray(activeDatabase?.automations?.rules)
      ? activeDatabase.automations.rules
      : [];

    if (!resolvedBaseKey || !typeKey) {
      setRules(hydrateRules(fallbackRules));
      return undefined;
    }

    const unsubscribe = subscribeToAutomationRules(
      { baseKey: resolvedBaseKey, nodeType: typeKey },
      (rows) => {
        if (Array.isArray(rows) && rows.length > 0) {
          setRules(hydrateRules(rows));
          return;
        }

        setRules(hydrateRules(fallbackRules));
      }
    );

    return () => unsubscribe?.();
  }, [activeDatabase?.automations?.rules, hydrateRules, isOpen, resolvedBaseKey, typeKey]);

  const sortedRules = useMemo(() => {
    return [...rules].sort(compareAutomationRules);
  }, [rules]);

  const buildNewRule = useCallback(() => {
    return buildDefaultRule({ databaseKey, typeKey, defaultFieldKey });
  }, [databaseKey, defaultFieldKey, typeKey]);

  const persistRules = useCallback(async (nextRules, successMessage) => {
    if (!databaseKey || !resolvedBaseKey || !typeKey || !userKey) {
      toast.error('Missing automation context.');
      return false;
    }

    const baseOnlyNextRules = (Array.isArray(nextRules) ? nextRules : [])
      .filter((rule) => !isSystemTypeAutomation(rule));
    const baseOnlyCurrentRules = (Array.isArray(rules) ? rules : [])
      .filter((rule) => !isSystemTypeAutomation(rule));

    const normalizedRules = normalizeRulesForSave({
      rules: baseOnlyNextRules,
      databaseKey,
      typeKey,
      fieldTypeByKey,
      fieldEnumOptionsByKey,
    });

    const payload = {
      automations: {
        version: 1,
        savedAtMs: Date.now(),
        rules: normalizedRules,
      },
    };

    const nextRuleKeySet = new Set(normalizedRules.map((rule) => String(rule?.key || '').trim()).filter(Boolean));
    const removedRuleKeys = baseOnlyCurrentRules
      .map((rule) => String(rule?.key || '').trim())
      .filter((ruleKey) => ruleKey && !nextRuleKeySet.has(ruleKey));

    try {
      setIsSaving(true);

      await Promise.all([
        ...normalizedRules.map((rule) => saveAutomationRule({
          rule,
          userKey,
          baseKey: resolvedBaseKey,
          nodeType: typeKey,
        })),
        ...removedRuleKeys.map((ruleKey) => deleteAutomationRuleByKey(ruleKey)),
      ]);

      await clearLegacyDatabaseAutomations({
        baseKey: resolvedBaseKey,
        typeKey,
      });

      // Keep live system rules in local state; only replace base rows.
      const systemRules = (Array.isArray(rules) ? rules : []).filter((rule) => (
        isSystemTypeAutomation(rule)
      ));
      setRules(hydrateRules([...systemRules, ...normalizedRules]));
      onSaved?.(payload);
      if (successMessage) {
        toast.success(successMessage);
      }
      return true;
    } catch (error) {
      console.error('Failed to save automations:', error);
      toast.error(error?.message || 'Could not save automations.');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [databaseKey, fieldEnumOptionsByKey, fieldTypeByKey, hydrateRules, onSaved, resolvedBaseKey, rules, typeKey, userKey]);

  const saveRule = useCallback(async (nextRule) => {
    if (isSystemTypeAutomation(nextRule)) {
      toast.error('System automations are edited in Admin → Types → Automations.');
      return false;
    }

    const normalizedRuleKey = String(nextRule?.key || '').trim();
    if (!normalizedRuleKey) return false;

    const exists = rules.some((rule) => rule.key === normalizedRuleKey);
    const nextRules = exists
      ? rules.map((rule) => (rule.key === normalizedRuleKey ? nextRule : rule))
      : [...rules, nextRule];

    return persistRules(nextRules, exists ? 'Automation updated.' : 'Automation added.');
  }, [persistRules, rules]);

  const deleteRule = useCallback(async (ruleKey) => {
    const normalizedRuleKey = String(ruleKey || '').trim();
    if (!normalizedRuleKey) return false;

    const target = rules.find((rule) => rule.key === normalizedRuleKey);
    if (isSystemTypeAutomation(target)) {
      toast.error('System automations are edited in Admin → Types → Automations.');
      return false;
    }

    const nextRules = rules.filter((rule) => rule.key !== normalizedRuleKey);
    return persistRules(nextRules, 'Automation deleted.');
  }, [persistRules, rules]);

  return {
    rules,
    sortedRules,
    isSaving,
    fieldOptions,
    ruleFieldOptions,
    fieldTypeByKey,
    fieldEnumOptionsByKey,
    buildNewRule,
    saveRule,
    deleteRule,
  };
}